import {
    cloneElement,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    GooglePlaceData,
    GooglePlaceDetail,
    GooglePlacesAutocompleteProps,
    GooglePlacesAutocompleteRef,
    PlaceType,
} from "react-native-google-places-autocomplete";
import { useScript } from "use-script";
import usePlacesAutocomplete, { Suggestions, getGeocode } from "use-places-autocomplete";
import { TextInput, View } from "react-native";

const CALLBACK_NAME = "addressChooserCallback";

type Props = Omit<GooglePlacesAutocompleteProps, "query"> & {
    query: { key: string; language: string; components: string; type: string };
    onLocationEnd?: () => void;
};

// eslint-disable-next-line react/display-name
export const GooglePlacesAutocomplete = forwardRef<
    Pick<
        GooglePlacesAutocompleteRef,
        "focus" | "clear" | "blur" | "isFocused" | "setAddressText" | "getCurrentLocation"
    >,
    Props
>(({ textInputProps, query, renderRow, onPress, placeholder, onLocationEnd }, ref) => {
    const [locationSuggestions, setLocationSuggestions] = useState<Suggestions["data"]>([]);

    const { init, value, setValue, suggestions, clearSuggestions } = usePlacesAutocomplete({
        callbackName: CALLBACK_NAME,
        requestOptions: {
            language: query.language,
            componentRestrictions: {
                country: query.components
                    ?.split("|")
                    .filter((v) => v.startsWith("country:"))
                    .map((v) => v.replace("country:", "")),
            },
            types: [query.type],
        },
    });

    const { loading: scriptLoading } = useScript({
        src: `https://maps.googleapis.com/maps/api/js?key=${query.key}&libraries=places&callback=${CALLBACK_NAME}`,
    });

    useEffect(() => {
        if (!scriptLoading) {
            init();
        }
    }, [init, scriptLoading]);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    const HeaderComp = useMemo(() => textInputProps.InputComp, [textInputProps.InputComp]);

    const inputRef = useRef<TextInput>(null);

    const getCurrentLocation = () => {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        navigator.geolocation.getCurrentPosition(async (position) => {
            const geocode = await getGeocode({
                location: new google.maps.LatLng(
                    position.coords.latitude,
                    position.coords.longitude,
                ),
            });
            setLocationSuggestions(
                geocode
                    .filter(
                        (code) =>
                            code.geometry.location_type ===
                            google.maps.GeocoderLocationType.ROOFTOP,
                    )
                    .map((code) => ({
                        ...code,
                        isCurrentLocation: true,
                        description: code.formatted_address,
                        structured_formatting: {
                            main_text:
                                code.address_components.find((a) => a.types.includes("route"))
                                    ?.long_name || "",
                            main_text_matched_substrings: [],
                            secondary_text: "",
                        },
                        matched_substrings: [],
                        terms: [],
                    })),
            );
            clearSuggestions();
            onLocationEnd?.();
        });
    };

    useImperativeHandle(ref, () => ({
        blur: () => inputRef.current?.blur(),
        focus: () => inputRef.current?.focus(),
        isFocused: () => inputRef.current?.isFocused() || false,
        clear: () => inputRef.current?.clear(),
        setAddressText: (initialValue) => {
            setValue(initialValue);
        },
        getCurrentLocation,
    }));

    return (
        <>
            <View style={{ flex: 1 }}>
                <HeaderComp
                    {...textInputProps}
                    ref={inputRef}
                    value={value}
                    onChangeText={setValue}
                    placeholder={placeholder}
                />
                <View style={{ flex: 1 }}>
                    {renderRow &&
                        (suggestions.data.length > 0 ? suggestions.data : locationSuggestions).map(
                            (value, index) => {
                                const place = {
                                    ...value,
                                    id: value.place_id,
                                    reference: value.description,
                                    structured_formatting: {
                                        ...value.structured_formatting,
                                        main_text_matched_substrings: [
                                            value.structured_formatting
                                                .main_text_matched_substrings,
                                        ],
                                        secondary_text_matched_substrings: [
                                            [{ text: value.structured_formatting.secondary_text }],
                                        ],
                                        terms: value.terms,
                                        types: value.types as PlaceType[],
                                    },
                                } as GooglePlaceData;

                                const rowItem = renderRow(place, index) as JSX.Element;

                                return cloneElement(rowItem, {
                                    onPress: async () => {
                                        const geoCode = await getGeocode({
                                            placeId: value.place_id,
                                        });
                                        onPress?.(place, {
                                            ...place,
                                            ...geoCode[0],
                                        } as unknown as GooglePlaceDetail);
                                    },
                                });
                            },
                        )}
                </View>
            </View>
        </>
    );
});
